import type { Block, Inline, Leaf, Text } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';
import {
    type DiffStrategy,
    type DiffTreeChange,
    type DiffTreeModification,
    diffTrees,
} from '@gitbook/util-tree-merge';

import { annotateAllTextWithDiff, annotateTextWithDiff } from './annotate';
import { diffTexts } from './diffTexts';
import { compareSlateNodes, isDataEqual } from './utils';

const diffStrategy: DiffStrategy<Inline | Text> = {
    getId: (node) => node.key,
    getChildren: () => [],
    setChildren: (node) => node,
    compareItems: compareSlateNodes,
    annotateChange: annotateChangeForInlines,
};

/**
 * Compute diff between two documents.
 */
export function diffInlines<T extends Block | Inline>(
    original: T,
    modified: T
): List<Inline | Text | Leaf> {
    const result = diffTrees(
        {
            before: getInlineNodes(original),
            after: getInlineNodes(modified),
        },
        diffStrategy
    );
    return List.create(result.items.map((node) => node.regenerateKey()));
}

function getInlineNodes(node: Block | Inline): Array<Inline | Text> {
    let textId = 0;

    return (node.nodes.toArray() as Array<Inline | Text>).map((child) => {
        if (child.object === 'text') {
            textId += 1;
            // For texts, we don't want to rely on the keys for identifying the nodes as it can often change
            // Instead we use the index of the text in the parent node
            return child.set('key', `${textId}`);
        }

        return child;
    });
}

function annotateChangeForInlines<T extends Inline | Text>(
    node: T,
    change: DiffTreeChange<T>
): Array<Inline | Text> {
    // Recursive diffing of inlines when only the content has changed
    if (node.object === 'inline' && change.type === 'modified') {
        const { before, item } = change as DiffTreeModification<Inline>;
        if (isDataEqual(before.data, item.data)) {
            return [node.merge({ nodes: diffInlines(before, item) })];
        }
        return [annotateAllTextWithDiff<Inline>(node, change.type)];
    }

    // Diffing for texts
    if (change.type === 'modified' && node.object === 'text') {
        const { before, item } = change as DiffTreeModification<Text>;
        return [diffTexts(before, item)];
    }

    return [
        node.object === 'text'
            ? annotateTextWithDiff(node, change.type)
            : annotateAllTextWithDiff<Inline>(node, change.type),
    ];
}
